/* eslint-disable no-console */
import { useState, useEffect, useCallback } from "react";
import toast from "react-hot-toast";
import { Modal, ModalHeader, ModalBody, ModalFooter, Button } from "reactstrap";
import { CUENTAS_ER } from "../../constants/clasificacionContable";
import { useAuthContext } from "../../context/auth";
import { getEmpleados } from "../../utils/empleados";
import { crearMovimiento } from "../../utils/flujoCaja";
import { confirmarMovimiento, actualizarMatch } from "../../utils/movimientosBancarios";
import { getProveedores } from "../../utils/proveedores";
import { client } from "../../utils/supabase";

// ─── Helpers ─────────────────────────────────────────────

const formatMonto = (monto, moneda = "CLP") => {
  if (monto == null) return "-";
  const num = Number(monto);
  if (moneda === "USD") return `US$ ${num.toLocaleString("es-CL", { maximumFractionDigits: 0 })}`;
  return `$ ${num.toLocaleString("es-CL", { maximumFractionDigits: 0 })}`;
};

const formatFecha = (fechaStr) => {
  if (!fechaStr) return "-";
  if (fechaStr.includes("T")) fechaStr = fechaStr.substring(0, 10);
  const [year, month, day] = fechaStr.split("-");
  return `${day}/${month}/${year}`;
};

const CATEGORIAS_PAGO = [
  { value: "pago_proveedor", label: "Proveedor" },
  { value: "pago_factura", label: "Pago con Factura" },
  { value: "sueldo", label: "Sueldo" },
  { value: "comision_edificio", label: "Comisión Edificio" },
  { value: "gasto_recurrente", label: "Gasto Recurrente" },
  { value: "costo_financiero", label: "Costo Financiero" },
  { value: "otro", label: "Otro" },
];

// Agrupar CUENTAS_ER por grupo
const CUENTAS_POR_GRUPO = {};
CUENTAS_ER.forEach((c) => {
  if (!CUENTAS_POR_GRUPO[c.grupo]) CUENTAS_POR_GRUPO[c.grupo] = [];
  CUENTAS_POR_GRUPO[c.grupo].push(c);
});

// ─── Componente Modal ────────────────────────────────────

const CorregirMovimientoModal = ({ isOpen, toggle, movimiento, onCorregido }) => {
  const { pais, id: userId } = useAuthContext();
  const enteFiscal = pais === "Chile" ? "SII" : "SUNAT";

  // Entidades
  const [proveedores, setProveedores] = useState([]);
  const [clientes, setClientes] = useState([]);
  const [empleados, setEmpleados] = useState([]);
  const [edificios, setEdificios] = useState([]);
  const [cargando, setCargando] = useState(false);

  // Selección
  const [entidadTipo, setEntidadTipo] = useState("");
  const [entidadId, setEntidadId] = useState("");
  const [busqueda, setBusqueda] = useState("");
  const [cuentaER, setCuentaER] = useState("");
  const [categoriaPago, setCategoriaPago] = useState("");
  const [facturaSeleccionada, setFacturaSeleccionada] = useState(null);
  const [facturasEntidad, setFacturasEntidad] = useState([]);
  const [guardando, setGuardando] = useState(false);

  // Factura externa
  const [esFacturaExterna, setEsFacturaExterna] = useState(false);
  const [extConcepto, setExtConcepto] = useState("");
  const [extNumeroDoc, setExtNumeroDoc] = useState("");
  const [extMoneda, setExtMoneda] = useState("USD");
  const [extArchivo, setExtArchivo] = useState(null);
  const [subiendoArchivo, setSubiendoArchivo] = useState(false);

  // Cargar entidades al abrir
  const cargarEntidades = useCallback(async () => {
    if (!pais || pais === "Region") return;
    setCargando(true);
    try {
      const [provs, clis, emps, edifs] = await Promise.all([
        getProveedores(pais),
        client
          .from("clientes")
          .select("id, nombre, empresa, rut")
          .eq("pais", pais)
          .then((r) => r.data || []),
        getEmpleados(pais),
        client
          .from("edificios")
          .select("id, nombre")
          .eq("pais", pais)
          .eq("estado", "OPERATIVO")
          .then((r) => r.data || []),
      ]);
      setProveedores(provs);
      setClientes(clis);
      setEmpleados(emps);
      setEdificios(edifs);
    } catch (err) {
      console.error("Error cargando entidades:", err);
    } finally {
      setCargando(false);
    }
  }, [pais]);

  useEffect(() => {
    if (isOpen && movimiento) {
      setEntidadTipo(movimiento.tipo === "egreso" ? "proveedor" : "cliente");
      setEntidadId("");
      setBusqueda("");
      setCuentaER("");
      setCategoriaPago(movimiento.tipo === "egreso" ? "pago_proveedor" : "");
      setFacturaSeleccionada(null);
      setFacturasEntidad([]);
      setEsFacturaExterna(false);
      setExtConcepto("");
      setExtNumeroDoc("");
      setExtMoneda("USD");
      setExtArchivo(null);
      cargarEntidades();
    }
  }, [isOpen, movimiento, cargarEntidades]);

  // Lista de entidades según tipo seleccionado
  const entidadesLista = (() => {
    const mapa = { proveedor: proveedores, cliente: clientes, empleado: empleados, edificio: edificios };
    return mapa[entidadTipo] || [];
  })();

  // Filtrar por búsqueda
  const entidadesFiltradas = busqueda
    ? entidadesLista.filter((e) => (e.nombre || e.empresa || "").toLowerCase().includes(busqueda.toLowerCase()))
    : entidadesLista;

  const entidadSeleccionada = entidadesLista.find((e) => e.id === Number(entidadId));
  const entidadNombre = entidadSeleccionada?.nombre || entidadSeleccionada?.empresa || "";

  // Las facturas de proveedor para match manual se cargaban desde facturas_proveedor (tabla
  // retirada). Ya no hay fuente de facturas por entidad acá → lista siempre vacía. El match a
  // factura externa se resuelve por el flag esFacturaExterna más abajo.
  useEffect(() => {
    setFacturasEntidad([]);
  }, [entidadId, entidadTipo]);

  // Pre-seleccionar cuenta_er_default del proveedor
  useEffect(() => {
    if (entidadTipo === "proveedor" && entidadId) {
      const prov = proveedores.find((p) => p.id === Number(entidadId));
      if (prov?.cuenta_er_default && !cuentaER) {
        setCuentaER(prov.cuenta_er_default);
      }
    }
  }, [entidadId, entidadTipo, proveedores, cuentaER]);

  // ─── Upload archivo a Storage ──────────────────────────

  const subirArchivo = async (file) => {
    const ext = file.name.split(".").pop();
    const path = `externas/${Date.now()}_${Math.random().toString(36).slice(2)}.${ext}`;
    const { error } = await client.storage.from("facturas").upload(path, file);
    if (error) throw error;
    const { data } = client.storage.from("facturas").getPublicUrl(path);
    return data.publicUrl;
  };

  // ─── Confirmar corrección ──────────────────────────────

  const handleConfirmar = async () => {
    if (!entidadId) {
      toast.error("Seleccioná una entidad");
      return;
    }
    if (esFacturaExterna && !extConcepto) {
      toast.error("Ingresá el concepto de la factura externa");
      return;
    }
    setGuardando(true);
    try {
      // Upload archivo si hay
      let archivoUrl = null;
      if (esFacturaExterna && extArchivo) {
        setSubiendoArchivo(true);
        archivoUrl = await subirArchivo(extArchivo);
        setSubiendoArchivo(false);
      }

      // 1. Si es factura externa, crear registro
      let facturaExternaId = null;
      if (esFacturaExterna) {
        const { data: fe, error: feErr } = await client
          .from("facturas_externas")
          .insert({
            pais,
            proveedor_id: Number(entidadId),
            proveedor_nombre: entidadNombre,
            concepto: extConcepto,
            numero_documento: extNumeroDoc || null,
            fecha_emision: movimiento.fecha,
            monto_neto: movimiento.monto,
            monto_impuesto: 0,
            monto_total: movimiento.monto,
            moneda: extMoneda,
            cuenta_er: cuentaER || null,
            archivo_url: archivoUrl,
            estado: "pagada",
            movimiento_bancario_id: movimiento.id,
            registrado_por: userId,
          })
          .select()
          .single();
        if (feErr) throw feErr;
        facturaExternaId = fe.id;
      }

      // 2. Actualizar match en movimiento_bancario
      const matchData = {
        match_entidad_tipo: entidadTipo,
        match_entidad_id: Number(entidadId),
        match_entidad_nombre: entidadNombre,
        match_factura_tipo: esFacturaExterna ? "factura_externa" : null,
        match_factura_id: facturaExternaId || null,
        match_confianza: 1,
        match_razon: esFacturaExterna ? "factura_externa" : "correccion_manual",
      };
      await actualizarMatch(movimiento.id, matchData);

      // 3. Crear movimiento_caja
      const cajaData = {
        pais,
        tipo: movimiento.tipo === "ingreso" ? "cobro" : "pago",
        fecha: movimiento.fecha,
        monto: movimiento.monto,
        monto_neto: movimiento.monto,
        monto_impuesto: 0,
        moneda: movimiento.moneda,
        estado: "confirmado",
        descripcion: esFacturaExterna ? `${entidadNombre} | ${extConcepto}` : movimiento.descripcion_raw,
        entidad_tipo: entidadTipo,
        entidad_id: Number(entidadId),
        entidad_nombre: entidadNombre,
        categoria_pago: categoriaPago || "pago_proveedor",
        origen: "clay",
        movimiento_bancario_id: movimiento.id,
        registrado_por: userId,
      };
      const nuevoCaja = await crearMovimiento(cajaData);
      if (!nuevoCaja?.id) throw new Error("No se pudo crear el movimiento en caja");

      // 4. Confirmar movimiento bancario
      await confirmarMovimiento(movimiento.id, { movimiento_caja_id: nuevoCaja.id, procesado_por: userId });

      toast.success(esFacturaExterna ? "Factura externa registrada y movimiento confirmado" : "Movimiento confirmado");
      onCorregido?.();
      toggle();
    } catch (err) {
      console.error("Error corrigiendo:", err);
      toast.error("Error al corregir movimiento");
    } finally {
      setGuardando(false);
      setSubiendoArchivo(false);
    }
  };

  if (!movimiento) return null;

  return (
    <Modal isOpen={isOpen} toggle={toggle} size="lg">
      <ModalHeader toggle={toggle}>Corregir Movimiento Bancario</ModalHeader>
      <ModalBody>
        {/* Detalle del movimiento */}
        <div
          style={{
            background: "#f8f9fa",
            padding: "12px 16px",
            borderRadius: 6,
            marginBottom: 16,
            fontSize: "0.82rem",
          }}
        >
          <div style={{ display: "flex", flexWrap: "wrap", gap: "16px 32px" }}>
            <div>
              <strong>Fecha:</strong> {formatFecha(movimiento.fecha)}
            </div>
            <div>
              <strong>Tipo:</strong> {movimiento.tipo}
            </div>
            <div>
              <strong>Moneda:</strong> {movimiento.moneda}
            </div>
            <div>
              <strong>Monto:</strong>{" "}
              <span style={{ fontWeight: 700, fontSize: "0.95rem" }}>
                {formatMonto(movimiento.monto, movimiento.moneda)}
              </span>
            </div>
          </div>
          <div style={{ marginTop: 6, color: "#666" }}>
            <strong>Descripción:</strong> {movimiento.descripcion_raw}
          </div>
        </div>

        {cargando && <div style={{ textAlign: "center", padding: 16, color: "#999" }}>Cargando entidades...</div>}

        {!cargando && (
          <>
            {/* Tipo de entidad */}
            <div style={{ marginBottom: 12 }}>
              <label style={{ fontSize: "0.75rem", fontWeight: 600, display: "block", marginBottom: 4 }}>
                Tipo de entidad
              </label>
              <div style={{ display: "flex", gap: 12, fontSize: "0.82rem" }}>
                {["proveedor", "cliente", "empleado", "edificio"].map((tipo) => (
                  <label key={tipo} style={{ cursor: "pointer" }}>
                    <input
                      type="radio"
                      name="entidadTipo"
                      value={tipo}
                      checked={entidadTipo === tipo}
                      onChange={() => {
                        setEntidadTipo(tipo);
                        setEntidadId("");
                        setBusqueda("");
                        setFacturaSeleccionada(null);
                        setCuentaER("");
                      }}
                      style={{ marginRight: 4 }}
                    />
                    {tipo.charAt(0).toUpperCase() + tipo.slice(1)}
                  </label>
                ))}
              </div>
            </div>

            {/* Buscador de entidad */}
            <div style={{ marginBottom: 12 }}>
              <label style={{ fontSize: "0.75rem", fontWeight: 600, display: "block", marginBottom: 4 }}>
                Buscar {entidadTipo}
              </label>
              <input
                type="text"
                value={busqueda}
                onChange={(e) => setBusqueda(e.target.value)}
                placeholder={`Escribí para buscar ${entidadTipo}...`}
                style={{
                  width: "100%",
                  height: 36,
                  fontSize: "0.82rem",
                  padding: "4px 10px",
                  border: "1px solid #ced4da",
                  borderRadius: 4,
                }}
              />
              {busqueda && entidadesFiltradas.length > 0 && !entidadId && (
                <div
                  style={{
                    border: "1px solid #dee2e6",
                    borderRadius: 4,
                    maxHeight: 150,
                    overflowY: "auto",
                    marginTop: 4,
                    fontSize: "0.78rem",
                  }}
                >
                  {entidadesFiltradas.slice(0, 15).map((e) => (
                    <div
                      key={e.id}
                      onClick={() => {
                        setEntidadId(e.id);
                        setBusqueda(e.nombre || e.empresa || "");
                      }}
                      style={{
                        padding: "6px 10px",
                        cursor: "pointer",
                        borderBottom: "1px solid #f0f0f0",
                      }}
                      onMouseEnter={(ev) => (ev.target.style.background = "#f8f9fa")}
                      onMouseLeave={(ev) => (ev.target.style.background = "transparent")}
                    >
                      {e.nombre || e.empresa}
                      {e.rut_ruc && <span style={{ color: "#999", marginLeft: 8 }}>({e.rut_ruc})</span>}
                      {e.rut && <span style={{ color: "#999", marginLeft: 8 }}>({e.rut})</span>}
                    </div>
                  ))}
                </div>
              )}
              {entidadId && entidadNombre && (
                <div style={{ fontSize: "0.78rem", marginTop: 4, color: "#27ae60" }}>
                  Seleccionado: <strong>{entidadNombre}</strong> (ID {entidadId})
                  <button
                    onClick={() => {
                      setEntidadId("");
                      setBusqueda("");
                      setFacturaSeleccionada(null);
                    }}
                    style={{
                      marginLeft: 8,
                      fontSize: "0.7rem",
                      border: "none",
                      background: "transparent",
                      color: "#c0392b",
                      cursor: "pointer",
                      textDecoration: "underline",
                    }}
                  >
                    Cambiar
                  </button>
                </div>
              )}
            </div>

            {/* Toggle factura externa (solo egresos + proveedor) */}
            {movimiento.tipo === "egreso" && entidadTipo === "proveedor" && entidadId && (
              <div style={{ marginBottom: 12 }}>
                <label style={{ fontSize: "0.78rem", cursor: "pointer" }}>
                  <input
                    type="checkbox"
                    checked={esFacturaExterna}
                    onChange={(e) => setEsFacturaExterna(e.target.checked)}
                    style={{ marginRight: 6 }}
                  />
                  Factura externa (proveedor sin {enteFiscal})
                </label>
              </div>
            )}

            {/* Campos factura externa */}
            {esFacturaExterna && (
              <div
                style={{
                  border: "1px solid #aed6f1",
                  borderRadius: 6,
                  padding: "12px 14px",
                  marginBottom: 12,
                  background: "#eaf2f8",
                }}
              >
                <div style={{ fontWeight: 600, fontSize: "0.78rem", marginBottom: 8 }}>Datos factura externa</div>
                <div style={{ display: "flex", gap: 10, marginBottom: 8 }}>
                  <div style={{ flex: 2 }}>
                    <label style={{ fontSize: "0.7rem", fontWeight: 600, display: "block", marginBottom: 2 }}>
                      Concepto *
                    </label>
                    <input
                      type="text"
                      value={extConcepto}
                      onChange={(e) => setExtConcepto(e.target.value)}
                      placeholder="Ej: Google Workspace marzo 2026"
                      style={{
                        width: "100%",
                        height: 32,
                        fontSize: "0.8rem",
                        padding: "4px 8px",
                        border: "1px solid #ced4da",
                        borderRadius: 4,
                      }}
                    />
                  </div>
                  <div style={{ flex: 1 }}>
                    <label style={{ fontSize: "0.7rem", fontWeight: 600, display: "block", marginBottom: 2 }}>
                      N° Documento
                    </label>
                    <input
                      type="text"
                      value={extNumeroDoc}
                      onChange={(e) => setExtNumeroDoc(e.target.value)}
                      placeholder="Invoice #"
                      style={{
                        width: "100%",
                        height: 32,
                        fontSize: "0.8rem",
                        padding: "4px 8px",
                        border: "1px solid #ced4da",
                        borderRadius: 4,
                      }}
                    />
                  </div>
                </div>
                <div style={{ display: "flex", gap: 10, marginBottom: 8 }}>
                  <div style={{ flex: 1 }}>
                    <label style={{ fontSize: "0.7rem", fontWeight: 600, display: "block", marginBottom: 2 }}>
                      Moneda original
                    </label>
                    <select
                      value={extMoneda}
                      onChange={(e) => setExtMoneda(e.target.value)}
                      style={{
                        width: "100%",
                        height: 32,
                        fontSize: "0.8rem",
                        padding: "4px 8px",
                        border: "1px solid #ced4da",
                        borderRadius: 4,
                      }}
                    >
                      <option value="USD">USD</option>
                      <option value="CLP">CLP</option>
                      <option value="PEN">PEN</option>
                      <option value="UF">UF</option>
                      <option value="EUR">EUR</option>
                    </select>
                  </div>
                  <div style={{ flex: 2 }}>
                    <label style={{ fontSize: "0.7rem", fontWeight: 600, display: "block", marginBottom: 2 }}>
                      PDF / Comprobante
                    </label>
                    <input
                      type="file"
                      accept=".pdf,.png,.jpg,.jpeg"
                      onChange={(e) => setExtArchivo(e.target.files?.[0] || null)}
                      style={{ fontSize: "0.75rem" }}
                    />
                  </div>
                </div>
              </div>
            )}

            {/* Facturas pendientes de la entidad */}
            {!esFacturaExterna && facturasEntidad.length > 0 && (
              <div style={{ marginBottom: 12 }}>
                <label style={{ fontSize: "0.75rem", fontWeight: 600, display: "block", marginBottom: 4 }}>
                  Facturas pendientes del proveedor
                </label>
                <div style={{ border: "1px solid #dee2e6", borderRadius: 4, fontSize: "0.78rem" }}>
                  {facturasEntidad.map((f) => (
                    <div
                      key={f.id}
                      onClick={() => setFacturaSeleccionada(facturaSeleccionada?.id === f.id ? null : f)}
                      style={{
                        padding: "6px 10px",
                        cursor: "pointer",
                        borderBottom: "1px solid #f0f0f0",
                        display: "flex",
                        justifyContent: "space-between",
                        background: facturaSeleccionada?.id === f.id ? "#d5f5e3" : "transparent",
                      }}
                    >
                      <span>
                        #{f.numero_factura} — {formatFecha(f.fecha)}
                      </span>
                      <span style={{ fontWeight: 600 }}>{formatMonto(f.monto_total)}</span>
                    </div>
                  ))}
                </div>
              </div>
            )}

            {/* Categoría de pago */}
            {movimiento.tipo === "egreso" && (
              <div style={{ marginBottom: 12 }}>
                <label style={{ fontSize: "0.75rem", fontWeight: 600, display: "block", marginBottom: 4 }}>
                  Categoría de pago
                </label>
                <select
                  value={categoriaPago}
                  onChange={(e) => setCategoriaPago(e.target.value)}
                  style={{
                    width: "100%",
                    height: 36,
                    fontSize: "0.82rem",
                    padding: "4px 10px",
                    border: "1px solid #ced4da",
                    borderRadius: 4,
                  }}
                >
                  <option value="">— Seleccionar —</option>
                  {CATEGORIAS_PAGO.map((c) => (
                    <option key={c.value} value={c.value}>
                      {c.label}
                    </option>
                  ))}
                </select>
              </div>
            )}
          </>
        )}
      </ModalBody>
      <ModalFooter>
        <Button color="secondary" size="sm" onClick={toggle}>
          Cancelar
        </Button>
        <Button color="dark" size="sm" onClick={handleConfirmar} disabled={guardando || !entidadId}>
          {guardando
            ? subiendoArchivo
              ? "Subiendo archivo..."
              : "Guardando..."
            : esFacturaExterna
              ? "Registrar factura externa"
              : "Confirmar"}
        </Button>
      </ModalFooter>
    </Modal>
  );
};

export default CorregirMovimientoModal;
